import React, { useCallback, useEffect, useState } from 'react';
import { supabaseClient } from '../../../config/supabase';
import Button3D from '../../ui/Button3D';
import Modal3D from '../../ui/Modal3D';
import AutomationSelector from './AutomationSelector';
import { decideRequest, loadPolicy, loadQueue, missingFoundation, withdrawRequest } from './foundationApi';
import './eventsFoundation.css';

const dates = (e) => (e.end_date && e.end_date !== e.start_date ? `${e.start_date} – ${e.end_date}` : e.start_date);
const ORIGIN = { client: 'Started by the client', staff: 'Started by staff', import: 'Imported from a workbook' };

// Requested events: review, accept (which activates the chosen automations) or decline with a note to the client.
export default function RequestedEventsQueue({ client = supabaseClient, onCount, onOpenPlan }) {
    const [rows, setRows] = useState(null);
    const [policy, setPolicy] = useState(null);
    const [error, setError] = useState('');
    const [deciding, setDeciding] = useState(null); // { row, accept }
    const [note, setNote] = useState('');
    const [automation, setAutomation] = useState(null);
    const [busy, setBusy] = useState(false);

    const refresh = useCallback(async () => {
        try { const next = await loadQueue(client); setRows(Array.isArray(next) ? next : []); onCount?.(Array.isArray(next) ? next.length : 0); setError(''); }
        catch (e) { setRows([]); setError(missingFoundation(e) ? 'Requested events will appear after the Events Foundation database update.' : e.message); }
    }, [client, onCount]);
    useEffect(() => { refresh(); loadPolicy(client).then(setPolicy).catch(() => setPolicy(null)); }, [client, refresh]);

    const open = (row, accept, withdraw = false) => { setDeciding({ row, accept, withdraw }); setNote(''); setError(''); setAutomation({ preset: row.automation_preset, switches: row.automation || {} }); };
    const decide = async () => {
        if (!deciding.accept && !note.trim()) { setError('Add a note for the client.'); return; }
        setBusy(true); setError('');
        try {
            if (deciding.withdraw) await withdrawRequest(client, deciding.row.event_id, note);
            else await decideRequest(client, deciding.row.event_id, deciding.accept, note, deciding.accept ? automation.preset : null, deciding.accept ? automation.switches : null);
            setDeciding(null); await refresh();
        } catch (e) { setError(e.message); } finally { setBusy(false); }
    };

    return (
        <section className="ef-panel" aria-label="Requested events" data-testid="requested-events">
            <h3 className="ef-title">Requested events {rows ? `· ${rows.length}` : ''}</h3>
            {error && !deciding && <p role="alert" className="ef-problems">{error}</p>}
            {rows && !rows.length && !error && <p className="ef-muted">No requests are waiting. New client requests appear here.</p>}
            <div className="ef-queue">
                {rows?.map((row) => (
                    <article key={row.event_id} className="ef-card" data-testid="request-card">
                        <h4 className="ef-legend">{row.name}</h4>
                        <p className="ef-muted">{row.client} · {dates(row)} · {row.kit || row.kit_key} · {ORIGIN[row.origin] || row.origin}</p>
                        <p>Invited {row.counts?.invited ?? 0} · Day-1 pickups {row.counts?.arrivals ?? 0} · Daily {row.counts?.daily ?? 0} · Returns {row.counts?.returns ?? 0}
                            {row.counts?.source === 'entered' ? ' (head counts)' : ''} · {row.moves} day-plan moves · Automation: {row.automation_preset}</p>
                        <div className="ef-actions ef-actions--start">
                            {onOpenPlan && <Button3D variant="light" onClick={() => onOpenPlan(row)}>Review details</Button3D>}
                            {row.can_edit && <><Button3D variant="go" onClick={() => open(row, true)} data-testid="accept-request">Accept</Button3D>
                                <Button3D onClick={() => open(row, false)} data-testid="decline-request">Decline</Button3D>
                                <Button3D variant="light" onClick={() => open(row, false, true)} data-testid="withdraw-request">Withdraw</Button3D></>}
                        </div>
                    </article>
                ))}
            </div>
            {deciding && (
                <Modal3D portal title={deciding.accept ? `Accept “${deciding.row.name}”` : `${deciding.withdraw ? 'Withdraw' : 'Decline'} “${deciding.row.name}”`}
                    subtitle={deciding.accept ? 'Accepting turns on the automations below.' : 'Your note is shown to the client.'} onClose={() => setDeciding(null)}
                    actions={<><Button3D onClick={() => setDeciding(null)}>Cancel</Button3D><Button3D variant="go" onClick={decide} disabled={busy} data-testid="confirm-decision">{deciding.accept ? 'Accept request' : deciding.withdraw ? 'Withdraw request' : 'Decline request'}</Button3D></>}>
                    <div className="ef-panel">
                        {deciding.accept && policy && <AutomationSelector switches={policy.switches} value={automation} onChange={setAutomation} forClient={false} idPrefix="ef-accept" />}
                        <label className="ef-field"><span className="ef-field__label">Note to the client{deciding.accept ? ' (optional)' : ''}</span>
                            <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} data-testid="decision-note" /></label>
                        {error && <p role="alert" className="ef-problems">{error}</p>}
                    </div>
                </Modal3D>
            )}
        </section>
    );
}
